import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { defaultMargin } from 'components/BaseComponents/constants'
import SBox from 'components/opinionless/SBox'
import SFlex from 'components/opinionless/SFlex'
import SInputError from 'components/opinionless/SInputErrorV2'
import SInput from 'components/opinionless/SInputV2'
import SRadioLabel from 'components/opinionless/SRadioLabelV2'
import SRadio from 'components/opinionless/SRadioV2'
import SText from 'components/opinionless/SText'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import STheme, { SStyled, useSTheme } from 'styles/theme'
import { useAddApplicationAnswersMutation } from 'types/graphql'
import { ga4_useTitle } from 'utils/ga4'
import { formatMoney } from 'utils/money'
import XBanner from 'v2/components/XBanner'
import { GENERIC_ERRORS } from 'v2/constants'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'

interface Props {
    setCurrentStep?: (number) => void
    applicationId: any
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`
const InputRow = SStyled(SFlex)`
    flex-direction: row;

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            flex-direction: column;
            gap: 9px;
        }
    `}
`
export const SRadioLabelV2 = SStyled(SRadioLabel)`

    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-bottom: 9px;
        }
    `}
`
export const SRadioV2 = SStyled(SRadio)`
    ${(p) => `
    @media screen and (max-width: ${p.theme.breakpoints[3]}) {
        margin-top:0px;
        
    }
    `}
    ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin-right: 7px;
            width: 40px;
            height: 40px;
        }
    `}
`
export const anualIncomeData = {
    1: 14580,
    2: 19720,
    3: 24860,
    4: 30000,
    5: 35140,
    6: 40280,
    7: 45420,
    8: 50560,
    9: 55700,
    10: 60840,
    11: 65980,
    12: 71120,
    13: 76260,
    14: 81400,
}

const HouseholdIncomeEligibility: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)

    const applicationContext = useContext(ApplicationContext)
    useApplicationBasicInfo()
    const {
        loading: applicationAnswersLoading,
        refetch: refetchApplicationAnswers,
        getAnswersByCategory,
    } = useApplicationAnswers()

    useEffect(() => {
        refetchApplicationAnswers()
    }, [refetchApplicationAnswers])

    useHouseholdMembers()

    const { handleSubmit, register, errors, setError, setValue, getValues } = useForm()

    const [addApplicationAnswers, { loading: addApplicationAnswersLoading }] = useAddApplicationAnswersMutation()

    const anualIncome = formatMoney(anualIncomeData[applicationContext.householdMembers.length + 1])
    const [radioBtn, setRadioBtn] = useState(null)

    const incomeThresholdAnswer = useMemo(() => {
        const incomeThreshold = getAnswersByCategory('INCOME_THRESHOLD')?.[0]?.response

        setValue('LBEqOrLessThanHouseholdIncomeTresholdSelection', incomeThreshold)

        return incomeThreshold
    }, [applicationContext.answers])

    useEffect(() => {
        if (incomeThresholdAnswer !== undefined) {
            setRadioBtn(incomeThresholdAnswer)
        }
    }, [incomeThresholdAnswer])

    const handleLBIncomeTresholdSelection = (e) => {
        setValue('LBEqOrLessThanHouseholdIncomeTresholdSelection', e.target.getAttribute('value'))
        setRadioBtn(e.target.getAttribute('value'))
    }

    ga4_useTitle('Household Income Eligibility')

    const fontFamily = props.fontFamily

    const onSubmit = (values) => {
        addApplicationAnswers({
            variables: {
                answers: [
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'COMBINED_HOUSEHOLD_INCOME',
                        response: anualIncome,
                    },
                    {
                        personApplicationId: applicationContext.application.id,
                        category: 'INCOME_THRESHOLD',
                        response: values.LBEqOrLessThanHouseholdIncomeTresholdSelection,
                    },
                ],
            },
        }).then((response) => {
            if (response.data.addApplicationAnswers.success === true) {
                props.nextStep()
            } else {
                setError('lbHouseholdIncome', { type: 'oops', message: t('error') })
            }
        })
    }
    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.incomeEligibility,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(onSubmit)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                        marginBottom: '24px',
                    }}
                >
                    {t('long_beach_household_income_eligibility_subtitle')}
                </LBText>
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '400',
                        lineHeight: '140%',
                        color: theme.colors.gray400,
                        fontSize: '14px',
                        marginBottom: '24px',
                    }}
                    dangerouslySetInnerHTML={{
                        __html: t('long_beach_household_income_eligibility_description'),
                    }}
                />
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '700',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                    }}
                >
                    {t('long_beach_program_income_treshold_label')}
                </LBText>
                <SInput
                    name="houseHoldCombinedIncome"
                    id="houseHoldCombinedIncome"
                    disabled={true}
                    value={`${anualIncome} per year`}
                    sx={{
                        fontFamily: fontFamily.body,
                        borderRadius: '0px !important',
                        marginBottom: '24px',
                        backgroundColor: 'red',
                    }}
                    style={{
                        fontSize: '14px',
                        fontWeight: 'normal',
                        lineHeight: '20px',
                        color: theme.colors.gray400,
                        backgroundColor: theme.colors.grayUnselectedCheckboxBackground,
                    }}
                />

                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '700',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                    }}
                >
                    {t('long_beach_program_household_choices')}
                </LBText>
                <InputRow
                    id="long_beach_household_income_label"
                    sx={{ display: 'flex !important', flexDirection: 'column !important' }}
                >
                    <SRadioLabelV2
                        sx={{
                            fontFamily: fontFamily.body,
                            marginTop: defaultMargin,
                        }}
                    >
                        <SBox maxWidth={'35px'} marginRight={'11px'}>
                            <SRadioV2
                                name="LBEqOrLessThanHouseholdIncomeTresholdSelection"
                                id="LBEqOrLessThanHouseholdIncomeTresholdSelection"
                                type="radio"
                                value="long_beach_equal_or_less_than_household_income_treshold"
                                onChange={handleLBIncomeTresholdSelection}
                                checked={radioBtn === 'long_beach_equal_or_less_than_household_income_treshold'}
                                size={'26px'}
                                color={
                                    radioBtn === 'long_beach_equal_or_less_than_household_income_treshold'
                                        ? theme.colors.secondaryColor
                                        : STheme.colors.grayUnselectedRadioOutline
                                }
                                ref={register({
                                    required: GENERIC_ERRORS.REQUIRED_FIELD,
                                })}
                            />
                        </SBox>
                        <SText
                            sx={{ width: '100%' }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_equal_or_less_than_household_income_treshold', {
                                    householdCombinedIncome: `${anualIncome}`,
                                }),
                            }}
                        ></SText>
                    </SRadioLabelV2>
                    <SRadioLabelV2
                        sx={{
                            fontFamily: fontFamily.body,
                        }}
                    >
                        <SBox maxWidth={'35px'} marginRight={'11px'}>
                            <SRadioV2
                                name="LBEqOrLessThanHouseholdIncomeTresholdSelection"
                                id="LBEqOrLessThanHouseholdIncomeTresholdSelection"
                                type="radio"
                                value="long_beach_greater_than_household_income_treshold"
                                checked={radioBtn === 'long_beach_greater_than_household_income_treshold'}
                                onChange={handleLBIncomeTresholdSelection}
                                size={'26px'}
                                color={
                                    radioBtn === 'long_beach_greater_than_household_income_treshold'
                                        ? theme.colors.secondaryColor
                                        : STheme.colors.grayUnselectedRadioOutline
                                }
                                ref={register({
                                    required: GENERIC_ERRORS.REQUIRED_FIELD,
                                })}
                            />
                        </SBox>
                        <SText
                            sx={{ width: '100%' }}
                            dangerouslySetInnerHTML={{
                                __html: t('long_beach_greater_than_household_income_treshold', {
                                    householdCombinedIncome: `${anualIncome}`,
                                }),
                            }}
                        ></SText>
                    </SRadioLabelV2>
                </InputRow>
                {errors.LBEqOrLessThanHouseholdIncomeTresholdSelection && (
                    <SInputError
                        forElementId={'long_beach_household_income_label'}
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                    >
                        {t(errors.LBEqOrLessThanHouseholdIncomeTresholdSelection?.message)}
                    </SInputError>
                )}

                <LBButton
                    type={'submit'}
                    id={'continueButton'}
                    loading={addApplicationAnswersLoading}
                    title={t('continue')}
                    width={'100%'}
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('save_and_continue')}
                </LBButton>
                {errors.lbHouseholdIncome && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(errors.lbHouseholdIncome?.message) ||
                            t(errors.LBEqOrLessThanHouseholdIncomeTresholdSelection?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeEligibility
